<script lang="ts">
import Alert from '@/components/v2/Alert.vue';

export default {
  name: 'FlashMessage',
  components: { Alert },
};
</script>

<script setup lang="ts">
import { MutationPayload, Store } from 'vuex';
import { AppState, Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { inject, onMounted, computed, onBeforeUnmount } from 'vue';

const store = inject<Store<AppState>>('store');
const TIMEOUT = 4000;

let flashTimeout: NodeJS.Timeout;
let unsubscribeToStore: (() => void) | undefined;

const flashMessage = computed(() => {
  return store?.state.flashMessage;
});

const alertType = computed(() => {
  const color = flashMessage.value?.color;
  if (color === 'error') return 'error';
  if (color === 'success') return 'success';
  if (color === 'warning') return 'warning';
  return 'info';
});

const flashTopStyle = computed(() => {
  const headerHeight = store?.state.resizerRegistry?.['header']?.height ?? 0;
  const gap = 16;
  return { top: `${headerHeight + gap}px` };
});

onMounted(() => {
  unsubscribeToStore = store?.subscribe(handleConfigChange);
});

onBeforeUnmount(() => {
  unsubscribeToStore?.();
});

function handleConfigChange(mutation: MutationPayload) {
  if (mutation.type === StoreMutationType.SET_FLASH_MESSAGE && mutation.payload) {
    if (flashTimeout) {
      clearTimeout(flashTimeout);
    }

    flashTimeout = setTimeout(() => {
      clearFlash();
    }, TIMEOUT);
  }
}

function clearFlash() {
  Commit(StoreMutationType.SET_FLASH_MESSAGE, undefined);
}
</script>

<template>
  <div
    v-if="flashMessage"
    class="tw:fixed tw:right-4 tw:z-50 tw:max-w-md tw:min-w-80"
    :style="flashTopStyle">
    <Alert
      :type="alertType"
      closable
      @close="clearFlash">
      <span data-test="flashMessage">{{ flashMessage.text }}</span>
    </Alert>
  </div>
</template>
